// src/services/admin/admin-dashboardService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const adminDashboardApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin/dashboard/analysis`,   // 👈 admin dashboard mount point
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000
});

// Use the admin token here
adminDashboardApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const adminDashboardService = {
  /**
   * Fetch the full dashboard payload in one round-trip.
   *
   * @param {Object} params
   * @param {number} [params.months=8]     - number of months for the revenue chart
   * @param {string} [params.status]       - filter recent orders by status label
   *                                          ('All status' | 'Pending' | 'Processing' |
   *                                           'Content review' | 'In production' |
   *                                           'Shipped' | 'Delivered' | 'Cancelled')
   * @param {string} [params.search]       - search recent orders by
   *                                          orderNumber / customer / item title
   * @param {number} [params.page=1]       - recent orders page number
   * @param {number} [params.limit=10]     - recent orders per page
   *
   * Response shape:
   *   {
   *     success: true,
   *     data: {
   *       stats: {
   *         totalRevenue:       { value, formatted, change },
   *         activeOrders:       { value, formatted, change },
   *         activeCustomers:    { value, formatted, change },
   *         futureGiftsPending: { value, formatted, change }
   *       },
   *       revenueSnapshot: {
   *         total, totalFormatted,
   *         currentMonth: { income, incomeFormatted, expense, orders, label, summary },
   *         series: [{ month, year, monthIndex, income, expense, orders }]
   *       },
   *       recentActivity: [{ type, user, time, iconColor, link }],
   *       recentOrders:   [{ id, orderId, date, customer, giftType, status,
   *                          statusColor, dotColor, bgColor, total, paymentStatus }],
   *       pagination:     { page, limit, total, pages, from, to },
   *       meta:           { generatedAt, monthsWindow, currency }
   *     }
   *   }
   */
  async getDashboardData(params = {}) {
    const res = await adminDashboardApi.get('/', { params });console.log("Dashboard ------", res.data)
    return res.data;
  },

  /**
   * Full revenue series only (optional dedicated endpoint).
   *
   * @param {Object} params
   * @param {number} [params.months=12] - number of months to include
   */
  async getRevenueSeries(params = {}) {
    const res = await adminDashboardApi.get('/revenue', { params }); 
    return res.data;
  },

  /* ───────── helpers ───────── */

  /**
   * Format a number as Nigerian Naira (compact for large values).
   *   1234     → ₦1,234
   *   184839   → ₦184.8K
   *   2400000  → ₦2.4M
   */
  formatNaira(amount) {
    const n = Number(amount) || 0;
    if (n >= 1_000_000) return `₦${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `₦${(n / 1_000).toFixed(1)}K`;
    return `₦${n.toLocaleString()}`;
  },

  /**
   * Format a percent change with sign and one decimal.
   *   12.5    → "+12.5%"
   *   -5.2    → "-5.2%"
   *   0       → "0.0%"
   */
  formatChange(pct) {
    const n = Number(pct) || 0;
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
  },

  /**
   * Returns 'increase' | 'decrease' for the change arrow/colour.
   */
  getChangeType(pct) {
    return Number(pct) >= 0 ? 'increase' : 'decrease';
  },

  /**
   * Colour tokens matching the dashboard UI.
   */
  getStatusBadgeConfig(status) {
    const configs = {
      pending:        { bgColor: 'bg-yellow-100', textColor: 'text-[#92400E]', dotColor: 'bg-[#92400E]' },
      processing:     { bgColor: 'bg-blue-100',   textColor: 'text-[#1E40AF]', dotColor: 'bg-[#1E40AF]' },
      content_review: { bgColor: 'bg-blue-100',   textColor: 'text-[#1E40AF]', dotColor: 'bg-[#1E40AF]' },
      in_production:  { bgColor: 'bg-yellow-100', textColor: 'text-[#92400E]', dotColor: 'bg-[#92400E]' },
      shipped:        { bgColor: 'bg-blue-100',   textColor: 'text-[#1E40AF]', dotColor: 'bg-[#1E40AF]' },
      delivered:      { bgColor: 'bg-[#E7F4ED]',  textColor: 'text-[#2F8F5B]', dotColor: 'bg-[#2F8F5B]' },
      cancelled:      { bgColor: 'bg-[#F4E7E7]',  textColor: 'text-[#8F342F]', dotColor: 'bg-[#8F342F]' }
    };
    return configs[status] || configs.pending;
  },

  /**
   * Icon + colour hint for a recent-activity entry.
   */
  getActivityIconConfig(type) {
    const configs = {
      order:    { iconColor: 'bg-[#6B635D]' },
      payment:  { iconColor: 'bg-[#2F8F5B]' },
      user:     { iconColor: 'bg-[#E2B457]' },
      shipped:  { iconColor: 'bg-[#FF8D28]' },
      upload:   { iconColor: 'bg-[#E2B457]' },
      gift:     { iconColor: 'bg-[#8F2F8A]' },
      default:  { iconColor: 'bg-[#6B635D]' }
    };
    return configs[type] || configs.default;
  },

  /**
   * Format a date string as 'YYYY-MM-DD' or a friendly label.
   */
  formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
};

export default adminDashboardService;